// Ubicación en el repo: src/lib/hearingaidQuotes.js
// Cotizaciones de audífonos (migración 0051, 26-ago-2026) — apartado
// imprimible dentro de Audífonos, pedido de Nicolás: ofrecer uno o varios
// modelos con precio a un paciente con ficha o a un prospecto que todavía
// no la tiene. Mismo criterio de acceso que hearingaids.js (RLS
// has_branch_access, cualquier rol con acceso a la sucursal puede
// crear/editar — no es ficha clínica, no exige Admin ni 2FA).
import { supabase } from './supabaseClient';

const QUOTE_SELECT =
  'id, branch_id, patient_id, prospect_name, prospect_phone, prospect_email, valid_until, ' +
  'payment_terms, notes, created_at, created_by, ' +
  'patients(nombre, apellido, segundo_nombre, segundo_apellido, rut, phone, email), ' +
  'professionals!created_by(nombre, apellido, role), ' +
  'branches(name, address, phone, logo_path)';

const QUOTE_ITEM_SELECT =
  'id, catalog_id, quoted_price, sort_order, ' +
  'catalog(id, brand, model, description, image_path, category:categories(name), brand_ref:brands(name))';

export async function listQuotes({ branchId, search } = {}) {
  if (!branchId) return [];
  let query = supabase
    .from('hearingaid_quotes')
    .select(QUOTE_SELECT)
    .is('deleted_at', null)
    .eq('branch_id', branchId)
    .order('created_at', { ascending: false });
  const { data, error } = await query;
  if (error) throw error;
  let rows = data ?? [];
  const term = search?.trim().toLowerCase();
  if (term) {
    rows = rows.filter((q) => {
      const patientName = q.patients
        ? [q.patients.nombre, q.patients.segundo_nombre, q.patients.apellido, q.patients.segundo_apellido]
            .filter(Boolean)
            .join(' ')
        : '';
      const name = (patientName || q.prospect_name || '').toLowerCase();
      return name.includes(term);
    });
  }
  return rows;
}

export async function getQuoteWithItems(id) {
  const [{ data: quote, error: quoteError }, { data: items, error: itemsError }] = await Promise.all([
    supabase.from('hearingaid_quotes').select(QUOTE_SELECT).eq('id', id).single(),
    supabase.from('hearingaid_quote_items').select(QUOTE_ITEM_SELECT).eq('quote_id', id).order('sort_order'),
  ]);
  if (quoteError) throw quoteError;
  if (itemsError) throw itemsError;
  return { ...quote, items: items ?? [] };
}

// `items` = [{ catalogId, quotedPrice }, ...] — al menos uno.
export async function createQuote(values, items, actorId) {
  const { data, error } = await supabase
    .from('hearingaid_quotes')
    .insert({
      branch_id: values.branchId,
      patient_id: values.patientId || null,
      prospect_name: values.patientId ? null : values.prospectName?.trim() || null,
      prospect_phone: values.patientId ? null : values.prospectPhone?.trim() || null,
      prospect_email: values.patientId ? null : values.prospectEmail?.trim() || null,
      valid_until: values.validUntil || null,
      payment_terms: values.paymentTerms?.trim() || null,
      notes: values.notes?.trim() || null,
      created_by: actorId,
      updated_by: actorId,
    })
    .select('id')
    .single();
  if (error) throw error;

  await insertItems(data.id, items);
  return data.id;
}

// Hallazgo #3 de la auditoría técnica del 11-sep-2026 (prioridad media,
// DECISIONS.md #121): antes esto hacía el update de la cotización, el
// delete de sus ítems y el insert de los nuevos como 3 llamadas separadas
// — si el insert fallaba a mitad de camino, la cotización quedaba con cero
// ítems. Ahora las 3 operaciones viven dentro de una sola función de
// servidor (`update_quote_with_items`, migración 0054), atómica de verdad:
// si algo falla, Postgres revierte todo, no puede quedar a medio camino.
export async function updateQuote(id, values, items) {
  const { error } = await supabase.rpc('update_quote_with_items', {
    p_quote_id: id,
    p_branch_id: values.branchId,
    p_patient_id: values.patientId || null,
    p_prospect_name: values.patientId ? null : values.prospectName?.trim() || null,
    p_prospect_phone: values.patientId ? null : values.prospectPhone?.trim() || null,
    p_prospect_email: values.patientId ? null : values.prospectEmail?.trim() || null,
    p_valid_until: values.validUntil || null,
    p_payment_terms: values.paymentTerms?.trim() || null,
    p_notes: values.notes?.trim() || null,
    p_items: items.map((it) => ({ catalogId: it.catalogId, quotedPrice: Number(it.quotedPrice) || 0 })),
  });
  if (error) throw new Error(error.message || 'No se pudo guardar la cotización.');
}

async function insertItems(quoteId, items) {
  const rows = items.map((it, i) => ({
    quote_id: quoteId,
    catalog_id: it.catalogId,
    quoted_price: Number(it.quotedPrice) || 0,
    sort_order: i,
  }));
  const { error } = await supabase.from('hearingaid_quote_items').insert(rows);
  if (error) throw error;
}

export async function softDeleteQuote(id, actorId) {
  const { error } = await supabase
    .from('hearingaid_quotes')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', id);
  if (error) throw error;
}
